import { QuaggaJSResultObject } from '@ericblade/quagga2';
import { Dims } from 'app/tools/general.types';
import { isValidBarcode, median } from 'app/tools/utils';
import _ from 'lodash';

export function getMedianOfCodeErrors(
  result: QuaggaJSResultObject
): number {
  const errors = result.codeResult.decodedCodes
    .filter(x => x.error !== undefined)
    .map(x => x.error as number);
  return median(errors);
}

export function quaggaResultIsValid(
  result: QuaggaJSResultObject
): boolean {
  const err = getMedianOfCodeErrors(result);
  const checksumValid = isValidBarcode(result.codeResult.code);
  // accept if > 91% certainty and checksum is valid
  return err < 0.09 && checksumValid;
}

export type CodeRead = Pick<
  QuaggaJSResultObject['codeResult'],
  'code' | 'format'
>;

export type WindowDimensions = {
  width: number;
  height: number;
  scanWindowSize: number;
};

const TARGET_STREAM_WIDTH = 1080;
const TARGET_STREAM_HEIGHT = 1440;

export const getTargetDims = (swap: boolean): Dims => ({
  width: swap ? TARGET_STREAM_HEIGHT : TARGET_STREAM_WIDTH,
  height: swap ? TARGET_STREAM_WIDTH : TARGET_STREAM_HEIGHT,
});

type Offsets = {
  top: number;
  bottom: number;
  left: number;
  right: number;
};
export type MatchedOffsets = Offsets & {
  hasMatched: boolean;
};
export const getOffsets = (offsets: {
  current: MatchedOffsets;
}): Offsets => {
  const { hasMatched, ...offsetQuantities } = offsets.current;
  return offsetQuantities;
};

/** Calculates the percentage offsets of the scan window, when overlaid
 * on top of the video feed */
// TODO: write tests for these functions
// NOTE: there are more efficient ways to do this, but I felt this was
// an easier method to understand and maintain.
export function calculateScanWindowOffsetPcts(
  window: WindowDimensions,
  video: Dims
): Offsets {
  const videoAspect = video.width / video.height;
  const windowAspect = window.width / window.height;

  // Finds which axes that are aligned, given that the video element
  // is sized to fill the window, and has css 'object-fit: cover;'
  const matchedAxis = videoAspect > windowAspect ? 'height' : 'width';
  // NOTE: multiply window px values by this scaling factor to get
  // their lengths when overlaid on top of the video stream.
  const scalingFactor = video[matchedAxis] / window[matchedAxis];

  const scanWindowInVideoPxs = window.scanWindowSize * scalingFactor;

  const topOffset =
    // scan window height is set to 2/3 of the scan window 'size'
    (video.height - scanWindowInVideoPxs * (2 / 3)) / 2;
  const topOffsetPct = (topOffset * 100) / video.height;

  const leftOffset = (video.width - scanWindowInVideoPxs) / 2;
  const leftOffsetPct = (leftOffset * 100) / video.width;

  return {
    top: _.round(topOffsetPct, 2),
    bottom: _.round(topOffsetPct, 2),
    left: _.round(leftOffsetPct, 2),
    right: _.round(leftOffsetPct, 2),
  };
}

export const getScanOffsetsFromTargetDims = (
  window: WindowDimensions,
  swap: boolean
): MatchedOffsets => ({
  ...calculateScanWindowOffsetPcts(window, getTargetDims(swap)),
  hasMatched: false,
});
